// mbscan manager-client へ同じ形で移植する予定（移植までは mbfab のみ）。移植後は片側だけで変えない
// （useFilterBarState の URL 同期で使う純粋ロジック。mbfab 固有の import を持たせない）

import { isEqual } from 'es-toolkit'

// URL に載せられる値。Date / dayjs は入れない（日付は 'YYYY-MM-DD' 文字列にする）。
// undefined を許すのは、zod 由来の optional なプロパティ（`{ a?: string }`）をそのまま状態にできるようにするため
// （JSON 化の際に落ちる）
export type JsonValue = string | number | boolean | null | undefined | JsonValue[] | { [key: string]: JsonValue }

export type JsonObject = { [key: string]: JsonValue }

type FilterBarParse<T extends JsonObject> = {
  /**
   * URL の値の検証。入れ子の状態や列挙型の状態は、キー単位の検証では守れないのでここで検証する。
   * 不正なら undefined を返すか例外を投げる（どちらも defaults に戻る）
   */
  parse?: (raw: unknown) => T | undefined
}

// urlKey を必須にするのは、1 画面（1 URL）に複数の一覧・タブがあっても条件が衝突しないよう、
// 一覧ごとに呼び出し側が名前を決める必要があるため（既定キーにすると取り合いに気づけない）
export type FilterBarUrlStateOptions<T extends JsonObject> =
  | (FilterBarParse<T> & {
      /** 省略・false は useState と同じ */
      syncUrl?: false
      urlKey?: undefined
    })
  | (FilterBarParse<T> & {
      /** true のとき、状態を URL のクエリに同期する */
      syncUrl: true
      /** クエリのキー名。「何の一覧の絞り込みか」が分かる camelCase の `<一覧名>Filter`（例: millsheetsFilter）。同時に表示する一覧と重複させない */
      urlKey: string
    })

/**
 * いまマウント中の urlKey の登録簿。同じキーが同時に 2 つ登録されると onDuplicate を呼ぶ。
 * 登録数で数えるのは、StrictMode の「マウント → 解除 → 再マウント」を重複と取り違えないため
 */
export function createUrlKeyRegistry(onDuplicate: (key: string) => void): (key: string) => () => void {
  const counts = new Map<string, number>()
  return (key) => {
    const next = (counts.get(key) ?? 0) + 1
    counts.set(key, next)
    if (next > 1) {
      onDuplicate(key)
    }
    let released = false
    return () => {
      if (released) {
        return
      }
      released = true
      const rest = (counts.get(key) ?? 1) - 1
      if (rest <= 0) {
        counts.delete(key)
      } else {
        counts.set(key, rest)
      }
    }
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

// defaults と同じ種類の値か。URL の値は利用者が書き換えられるので、型の合わないものは受けない。
// 入れ子のオブジェクトは扱わない（false を返し defaults へ戻す）。必要な状態は parse で検証する
const hasSameKind = <V extends JsonValue>(fallback: V, raw: unknown): raw is V => {
  const isScalar = (value: unknown): boolean =>
    typeof value === 'string' || typeof value === 'boolean' || (typeof value === 'number' && Number.isFinite(value))
  const isScalarArray = (value: unknown): boolean =>
    Array.isArray(value) &&
    (value.every((item) => typeof item === 'string') ||
      value.every((item) => typeof item === 'number' && Number.isFinite(item)))
  if (fallback === null) {
    return raw === null || isScalar(raw) || isScalarArray(raw)
  }
  if (Array.isArray(fallback)) {
    if (!isScalarArray(raw)) {
      return false
    }
    const first: unknown = fallback[0]
    return first === undefined || (Array.isArray(raw) && raw.every((item) => typeof item === typeof first))
  }
  if (typeof fallback === 'number') {
    return typeof raw === 'number' && Number.isFinite(raw)
  }
  return typeof fallback === typeof raw && (typeof fallback === 'string' || typeof fallback === 'boolean')
}

// URL の値の大きさの上限（canonical 文字列の長さ）。巨大な値を描画のたびに検証・比較しないため、超えたら信用せず defaults にする
export const maxFilterBarUrlValueLength = 4000

/** URL の値から状態を作る。信用せず、defaults に無いキーの破棄・型違いの defaults 戻しをする（例外を出さない） */
export function decodeFilterBarState<T extends JsonObject>(
  defaults: T,
  raw: unknown,
  parse?: (raw: unknown) => T | undefined,
): T {
  if (raw === undefined || canonicalizeFilterBarValue(raw).length > maxFilterBarUrlValueLength) {
    return defaults
  }
  if (parse !== undefined) {
    try {
      return parse(raw) ?? defaults
    } catch {
      return defaults
    }
  }
  if (!isRecord(raw)) {
    return defaults
  }
  const overrides: Record<string, JsonValue> = {}
  for (const [key, fallback] of Object.entries(defaults)) {
    const candidate = raw[key]
    overrides[key] = hasSameKind(fallback, candidate) ? candidate : fallback
  }
  return { ...defaults, ...overrides }
}

/** defaults と異なるキーだけを URL に載せる。全く同じなら undefined（URL からキーごと消す） */
export function encodeFilterBarState<T extends JsonObject>(state: T, defaults: T): JsonObject | undefined {
  const diff: JsonObject = {}
  for (const [key, value] of Object.entries(state)) {
    if (value !== undefined && !isEqual(value, defaults[key])) {
      diff[key] = value
    }
  }
  return Object.keys(diff).length === 0 ? undefined : diff
}

/** キーの順序に依存しない比較用の文字列。URL 由来と状態由来で順序が違っても一致させる。値なしは '' */
export function canonicalizeFilterBarValue(value: unknown): string {
  if (value === undefined) {
    return ''
  }
  const walk = (node: unknown): string => {
    if (Array.isArray(node)) {
      return `[${node.map(walk).join(',')}]`
    }
    if (isRecord(node)) {
      const entries = Object.keys(node)
        .sort()
        .filter((key) => node[key] !== undefined)
        .map((key) => `${JSON.stringify(key)}:${walk(node[key])}`)
      return `{${entries.join(',')}}`
    }
    return node === undefined ? 'null' : JSON.stringify(node)
  }
  return walk(value)
}
